import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Splide } from '@splidejs/react-splide';
import { useDeviceType, UIText1 } from '@cian/ui-kit';

import {
  IconActionChevronLeft16,
  IconActionChevronRight16,
  IconActionFullscreen16,
  IconActionClose16,
} from '@cian/ui-kit-design-tokens/icons';

import { ReactPortal } from '../ReactPortal';
import { ThumbsGallery } from './components/ThumbsGallery';
import { MainGallery } from './components/MainGallery';
import { IParsePhotoGalleryItem, IParsePhotoGallerySlideItem, useEventListener } from '../../utils';
import * as s from './PostPhotoGallery.css';

export interface IPostPhotoGallery {
  slides: IParsePhotoGallerySlideItem[];
  source?: IParsePhotoGalleryItem['source'];
  description?: IParsePhotoGalleryItem['description'];
  objectFit?: IParsePhotoGalleryItem['objectFit'];
}

/**
 * Фотогалерея в теле поста
 */
export const PostPhotoGallery = ({ slides, source, description, objectFit }: IPostPhotoGallery) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreenEnabled, setIsFullscreenEnabled] = useState(false);
  const [currentDescription, setCurrentDescription] = useState<string | undefined>(undefined);
  const [currentSource, setCurrentSource] = useState<string | undefined>(undefined);
  const mainGalleryRef = useRef<Splide>(null);
  const thumbsGalleryRef = useRef<Splide>(null);
  const fullscreenMainGalleryRef = useRef<Splide>(null);
  const fullscreenThumbsGalleryRef = useRef<Splide>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const fullscreenWrapperRef = useRef<HTMLDivElement>(null);
  const deviceType = useDeviceType();

  // Первый рендер совпадает с серверным (desktop) для корректной гидрации,
  // после монтирования переключаемся на реальный deviceType
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const effectiveDeviceType = mounted ? deviceType : 'desktop';
  const isPhone = effectiveDeviceType === 'phone';

  /** Синхронизирует основную и миниатюрную галереи */
  useEffect(() => {
    if (mainGalleryRef?.current && thumbsGalleryRef?.current?.splide) {
      mainGalleryRef.current.sync(thumbsGalleryRef.current.splide);
    }
  }, [deviceType]);

  /** Синхронизирует основную и полноэкранную галереи */
  useEffect(() => {
    document.body.style.overflow = isFullscreenEnabled ? 'hidden' : '';

    if (!isFullscreenEnabled) {
      return;
    }

    if (mainGalleryRef?.current && fullscreenMainGalleryRef?.current?.splide) {
      mainGalleryRef.current.sync(fullscreenMainGalleryRef.current.splide);
    }

    if (mainGalleryRef?.current && fullscreenThumbsGalleryRef?.current?.splide) {
      mainGalleryRef.current.sync(fullscreenThumbsGalleryRef.current.splide);
    }
  }, [isFullscreenEnabled]);

  /** Устанавливает текущее описание и источник */
  useEffect(() => {
    setCurrentDescription(slides[activeIndex].description || description);
    setCurrentSource(slides[activeIndex].source || source);
  }, [activeIndex, description, slides, source]);

  /** Плавно меняет размер блока описания/источника при их изменении */
  useEffect(() => {
    if (!descriptionRef?.current) {
      return;
    }

    const container = descriptionRef.current as HTMLElement;
    const innerContainer = container.children[0];

    container.style.height = `${innerContainer.getBoundingClientRect().height}px`;
  }, [currentDescription, currentSource]);

  /** Закрывает полноэкранную галерею при нажатии на Escape */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isFullscreenEnabled) {
        return;
      }

      if (e.code === 'Escape') {
        setIsFullscreenEnabled(false);
      }
    },
    [isFullscreenEnabled],
  );

  /** Слушает нажатие клавиш у полноэкранной галереи */
  useEventListener('keydown', handleKeyDown, fullscreenWrapperRef);

  return (
    <div className={s['wrapper']}>
      <MainGallery
        ref={mainGalleryRef}
        slides={slides}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onMounted={(gallery: any) => setActiveIndex(gallery.index)}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onMove={(gallery: any) => setActiveIndex(gallery.index)}
        description={description}
        objectFit={objectFit === 'cover' ? 'cover' : 'contain'}
        options={{ arrows: slides?.length > 1 }}
      >
        <div className={`splide__arrows ${s['arrows']}`}>
          <button className={`splide__arrow splide__arrow--prev ${s['arrow']} ${s['arrow-prev']}`}>
            <IconActionChevronLeft16 color={'icon-inverted-default'} />
          </button>

          <button className={`splide__arrow splide__arrow--next ${s['arrow']} ${s['arrow-next']}`}>
            <IconActionChevronRight16 color={'icon-inverted-default'} />
          </button>
        </div>

        <button className={s['fullscreen-button']} onClick={() => setIsFullscreenEnabled(true)}>
          <IconActionFullscreen16 color={'icon-inverted-default'} />
        </button>
      </MainGallery>

      {!isPhone && slides?.length > 1 && (
        <ThumbsGallery ref={thumbsGalleryRef} slides={slides} activeIndex={activeIndex} />
      )}

      <div ref={descriptionRef} className={s['description']}>
        <div className={s['description-inner']}>
          {currentDescription && <UIText1 color={'text-secondary-default'}>{currentDescription}</UIText1>}
          {currentSource && <UIText1 color={'text-secondary-default'}>Источник: {currentSource}</UIText1>}
        </div>
      </div>

      <ReactPortal wrapperId={'photo-gallery-portal'}>
        <div ref={fullscreenWrapperRef}>
          {isFullscreenEnabled && (
            <div className={s['fullscreen-overlay']}>
              <MainGallery
                ref={fullscreenMainGalleryRef}
                slides={slides}
                description={description}
                objectFit={'contain'}
                options={{
                  start: activeIndex,
                  width: '100vw',
                  height: 'calc(100vh - 200px)',
                  heightRatio: undefined,
                  arrows: slides?.length > 1,
                }}
              >
                <div className={`splide__arrows ${s['arrows']}`}>
                  <button className={`splide__arrow splide__arrow--prev ${s['arrow']} ${s['arrow-prev']}`}>
                    <IconActionChevronLeft16 color={'icon-inverted-default'} />
                  </button>

                  <button className={`splide__arrow splide__arrow--next ${s['arrow']} ${s['arrow-next']}`}>
                    <IconActionChevronRight16 color={'icon-inverted-default'} />
                  </button>
                </div>
              </MainGallery>

              <button className={s['close-button']} onClick={() => setIsFullscreenEnabled(false)}>
                <IconActionClose16 color={'icon-inverted-default'} />
              </button>

              <ThumbsGallery
                ref={fullscreenThumbsGalleryRef}
                slides={slides}
                activeIndex={activeIndex}
                options={{ start: activeIndex, width: '100vw', focus: undefined }}
              />
            </div>
          )}
        </div>
      </ReactPortal>
    </div>
  );
};
